import { useEffect, useState } from 'react'
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import { Card, Badge, InlineSpinner, EmptyState, Button, Modal, ModalBody, ModalFooter } from '@/components/ui'
import apiClient from '@/api/client'

interface Permission {
  id: number
  code: string
  name: string
  description: string
  module: string
  is_active: boolean
}

interface PermissionModule {
  module: string
  module_name: string
  permissions: Permission[]
}

interface RolePermission {
  id: number
  permission: number
  permission_code: string
  permission_name: string
  permission_module: string
  is_granted: boolean
}

interface Role {
  id: number
  code: string
  name: string
  description: string
  color: string
  icon: string
  is_system: boolean
  hierarchy_level: number
  permissions_count: number
  users_count: number
  role_permissions?: RolePermission[]
}

export default function AdminPermissionsPage() {
  const [roles, setRoles] = useState<Role[]>([])
  const [permissions, setPermissions] = useState<PermissionModule[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedRole, setExpandedRole] = useState<number | null>(null)
  const [expandedModules, setExpandedModules] = useState<string[]>([])

  // Create/Edit Role Modal
  const [showRoleModal, setShowRoleModal] = useState(false)
  const [editingRole, setEditingRole] = useState<Role | null>(null)
  const [roleForm, setRoleForm] = useState({
    code: '',
    name: '',
    description: '',
    color: '#6B7280',
    hierarchy_level: 50,
  })
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Delete Role Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [rolesRes, permissionsRes] = await Promise.all([
        apiClient.get('/admin/roles/'),
        apiClient.get('/admin/permissions/by_module/'),
      ])
      setRoles(rolesRes.data.results || rolesRes.data)
      setPermissions(permissionsRes.data)
    } catch (err) {
      console.error('Error fetching data:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchRoleDetails = async (roleId: number) => {
    try {
      const response = await apiClient.get(`/admin/roles/${roleId}/`)
      const roleData = response.data
      // Update the role in the list with full details
      setRoles(prev => prev.map(r => r.id === roleId ? { ...r, ...roleData } : r))
      return roleData
    } catch (err) {
      console.error('Error fetching role details:', err)
      return null
    }
  }

  const handleExpandRole = async (roleId: number) => {
    if (expandedRole === roleId) {
      setExpandedRole(null)
    } else {
      setExpandedRole(roleId)
      const role = roles.find(r => r.id === roleId)
      if (role && !role.role_permissions) {
        await fetchRoleDetails(roleId)
      }
    }
  }

  const toggleModule = (module: string) => {
    setExpandedModules(prev =>
      prev.includes(module) ? prev.filter(m => m !== module) : [...prev, module]
    )
  }

  const handleCreateRole = () => {
    setEditingRole(null)
    setRoleForm({
      code: '',
      name: '',
      description: '',
      color: '#6B7280',
      hierarchy_level: 50,
    })
    setSelectedPermissions([])
    setExpandedModules(permissions.map(m => m.module)) // Expand all modules
    setError('')
    setShowRoleModal(true)
  }

  const handleEditRole = async (role: Role) => {
    setEditingRole(role)
    setRoleForm({
      code: role.code,
      name: role.name,
      description: role.description,
      color: role.color,
      hierarchy_level: role.hierarchy_level,
    })

    // Fetch full role details to get permissions
    const fullRole = await fetchRoleDetails(role.id)
    if (fullRole?.role_permissions) {
      setSelectedPermissions(
        fullRole.role_permissions
          .filter((rp: RolePermission) => rp.is_granted)
          .map((rp: RolePermission) => rp.permission)
      )
    }

    setExpandedModules(permissions.map(m => m.module))
    setError('')
    setShowRoleModal(true)
  }

  const handleSaveRole = async () => {
    setSaving(true)
    setError('')

    try {
      const payload = {
        ...roleForm,
        permission_ids: selectedPermissions,
      }

      if (editingRole) {
        await apiClient.patch(`/admin/roles/${editingRole.id}/`, payload)
      } else {
        await apiClient.post('/admin/roles/', payload)
      }

      setShowRoleModal(false)
      fetchData()
    } catch (err: any) {
      console.error('Error saving role:', err)
      if (err.response?.data) {
        const data = err.response.data
        if (typeof data === 'object') {
          const messages = Object.entries(data)
            .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('; ')
          setError(messages)
        } else {
          setError(String(data))
        }
      } else {
        setError('Errore durante il salvataggio')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteRole = async () => {
    if (!roleToDelete) return
    setDeleting(true)

    try {
      await apiClient.delete(`/admin/roles/${roleToDelete.id}/`)
      setShowDeleteModal(false)
      setRoleToDelete(null)
      fetchData()
    } catch (err: any) {
      console.error('Error deleting role:', err)
      alert(err.response?.data?.detail || 'Errore durante l\'eliminazione')
    } finally {
      setDeleting(false)
    }
  }

  const togglePermission = (permId: number) => {
    setSelectedPermissions(prev =>
      prev.includes(permId) ? prev.filter(p => p !== permId) : [...prev, permId]
    )
  }

  const toggleModulePermissions = (module: PermissionModule) => {
    const modulePermIds = module.permissions.map(p => p.id)
    const allSelected = modulePermIds.every(id => selectedPermissions.includes(id))

    if (allSelected) {
      setSelectedPermissions(prev => prev.filter(id => !modulePermIds.includes(id)))
    } else {
      setSelectedPermissions(prev => [...new Set([...prev, ...modulePermIds])])
    }
  }

  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    role.code.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (loading) {
    return <InlineSpinner message="Caricamento ruoli e permessi..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Ruoli e Permessi</h1>
          <p className="text-gray-600">{roles.length} ruoli configurati</p>
        </div>
        <Button onClick={handleCreateRole}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Nuovo Ruolo
        </Button>
      </div>

      {/* Search */}
      <Card>
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca ruoli..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </Card>

      {/* Roles List */}
      {filteredRoles.length > 0 ? (
        <div className="space-y-4">
          {filteredRoles.map((role) => (
            <Card key={role.id} className="overflow-hidden">
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => handleExpandRole(role.id)}
              >
                <div className="flex items-center gap-4">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: role.color + '20' }}
                  >
                    <ShieldCheckIcon className="h-5 w-5" style={{ color: role.color }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{role.name}</h3>
                      {role.is_system && (
                        <Badge variant="info">Sistema</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{role.code}</p>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-lg font-semibold text-gray-900">{role.permissions_count}</p>
                    <p className="text-xs text-gray-500">Permessi</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-semibold text-gray-900">{role.users_count}</p>
                    <p className="text-xs text-gray-500">Utenti</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-semibold text-gray-900">{role.hierarchy_level}</p>
                    <p className="text-xs text-gray-500">Livello</p>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleEditRole(role); }}
                      className="p-2 text-gray-500 hover:bg-gray-100 rounded"
                      title="Modifica"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    {!role.is_system && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setRoleToDelete(role); setShowDeleteModal(true); }}
                        className="p-2 text-red-500 hover:bg-red-50 rounded"
                        title="Elimina"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                    {expandedRole === role.id ? (
                      <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Permissions */}
              {expandedRole === role.id && role.role_permissions && (
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                  <h4 className="font-medium text-gray-700 mb-3">Permessi assegnati:</h4>
                  {role.role_permissions.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {role.role_permissions
                        .filter(rp => rp.is_granted)
                        .map(rp => (
                          <Badge
                            key={rp.id}
                            variant="default"
                            className="text-xs"
                          >
                            {rp.permission_name}
                          </Badge>
                        ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Nessun permesso assegnato</p>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <EmptyState
            icon={<UserGroupIcon className="h-12 w-12" />}
            title="Nessun ruolo trovato"
            description="Non ci sono ruoli che corrispondono alla ricerca"
          />
        </Card>
      )}

      {/* Create/Edit Role Modal */}
      <Modal
        isOpen={showRoleModal}
        onClose={() => setShowRoleModal(false)}
        title={editingRole ? 'Modifica Ruolo' : 'Nuovo Ruolo'}
        size="lg"
      >
        <ModalBody>
          <div className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Codice *</label>
                <input
                  type="text"
                  value={roleForm.code}
                  onChange={(e) => setRoleForm({ ...roleForm, code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="es: content_editor"
                  disabled={editingRole?.is_system}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input
                  type="text"
                  value={roleForm.name}
                  onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="es: Editor Contenuti"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
              <textarea
                value={roleForm.description}
                onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Descrizione del ruolo"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Colore</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={roleForm.color}
                    onChange={(e) => setRoleForm({ ...roleForm, color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={roleForm.color}
                    onChange={(e) => setRoleForm({ ...roleForm, color: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Livello Gerarchia</label>
                <input
                  type="number"
                  value={roleForm.hierarchy_level}
                  onChange={(e) => setRoleForm({ ...roleForm, hierarchy_level: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  min={0}
                  max={100}
                />
                <p className="text-xs text-gray-500 mt-1">0-100, maggiore = piu permessi</p>
              </div>
            </div>

            {/* Permissions Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Permessi ({selectedPermissions.length} selezionati)
              </label>
              <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
                {permissions.map(module => {
                  const modulePermIds = module.permissions.map(p => p.id)
                  const selectedInModule = modulePermIds.filter(id => selectedPermissions.includes(id)).length
                  const isExpanded = expandedModules.includes(module.module)

                  return (
                    <div key={module.module} className="border-b border-gray-200 last:border-b-0">
                      <div
                        className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                        onClick={() => toggleModule(module.module)}
                      >
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                          )}
                          <span className="font-medium text-gray-700">{module.module_name}</span>
                          <Badge variant="default" className="text-xs">
                            {selectedInModule}/{module.permissions.length}
                          </Badge>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleModulePermissions(module); }}
                          className="text-xs text-primary-600 hover:text-primary-800"
                        >
                          {selectedInModule === modulePermIds.length ? 'Deseleziona tutti' : 'Seleziona tutti'}
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="p-2 space-y-1">
                          {module.permissions.map(perm => (
                            <label
                              key={perm.id}
                              className="flex items-start gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedPermissions.includes(perm.id)}
                                onChange={() => togglePermission(perm.id)}
                                className="mt-1 rounded border-gray-300"
                              />
                              <div>
                                <p className="text-sm font-medium text-gray-900">{perm.name}</p>
                                <p className="text-xs text-gray-500">{perm.code}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowRoleModal(false)}>
            Annulla
          </Button>
          <Button
            onClick={handleSaveRole}
            disabled={saving || !roleForm.code || !roleForm.name}
          >
            {saving ? 'Salvataggio...' : (editingRole ? 'Salva Modifiche' : 'Crea Ruolo')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Role Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Elimina Ruolo"
      >
        <ModalBody>
          {roleToDelete && (
            <div className="space-y-4">
              <p className="text-gray-600">
                Sei sicuro di voler eliminare il ruolo{' '}
                <span className="font-semibold">{roleToDelete.name}</span>?
              </p>
              {roleToDelete.users_count > 0 && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700 text-sm">
                  Attenzione: {roleToDelete.users_count} utenti hanno questo ruolo assegnato.
                  Dovrai riassegnarli prima di poter eliminare il ruolo.
                </div>
              )}
              <p className="text-sm text-red-600">
                Questa azione e irreversibile.
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
            Annulla
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteRole}
            disabled={deleting || (roleToDelete?.users_count || 0) > 0}
          >
            {deleting ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
